/**
 * SAY MY NAME — the explicit UI state model.
 *
 * The interface has four states, and every visual difference in the studio is
 * derived from one of them. They are computed from REAL evidence — which screen
 * is mounted, whether a run is genuinely in flight, whether a human question is
 * open, whether a decision has actually been written to the canvas — never from
 * anything the model asserts about itself.
 *
 *   understand  — the landing. The agent is present and waiting; the agent panel
 *                 owns the only input on the page.
 *   working     — a run is in flight right now. The status light breathes and
 *                 output streams into the canvas.
 *   needs_input — the run has ended and the ball is with the customer: a question
 *                 is open, or candidates are sitting there waiting for a
 *                 reaction. Brighter steady light.
 *   complete    — a decision block has been written: the recommendation is on the
 *                 canvas. Steady light.
 *
 * The state is also published to the DOM as `data-studio-state` on each screen's
 * root, which is what lets `theme.css` keep exactly ONE input on the landing by
 * hiding the platform's global composer while the agent panel owns the
 * interaction.
 */
import { observable, runInAction } from 'mobx';
import type { AgentStatus } from './surfaces/StatusLight.tsx';

export type StudioUiState = 'understand' | 'working' | 'needs_input' | 'complete';

/**
 * The process has exactly three phases — UNDERSTAND → DISCOVER → DECIDE — shown
 * as a restrained status indicator in the persistent header. There is no fourth.
 *
 * The phase is derived server-side from the blocks the agent actually wrote (see
 * `derivePhase` in surfaces/studio.ts) and published here so the header, which
 * lives outside the surface tree, can read it. The landing sets `understand`.
 */
export type StudioPhase = 'understand' | 'discover' | 'decide';

export const STUDIO_PHASES: readonly StudioPhase[] = ['understand', 'discover', 'decide'];

/** Observable so the header re-renders when the phase advances mid-engagement. */
const phaseBox = observable.box<StudioPhase>('understand');

const PHASE_ORDER: Record<StudioPhase, number> = { understand: 0, discover: 1, decide: 2 };

/**
 * The process only moves FORWARD. Each new turn opens a fresh surface whose first
 * blocks are early ones, so a naive assignment would snap the indicator back to
 * UNDERSTAND for a moment before catching up. Work already done is not undone, so
 * the phase never regresses inside an engagement — only `resetStudioPhase` (a new
 * session landing on the entry screen) takes it back to the start.
 */
export function setStudioPhase(phase: StudioPhase): void {
  if (PHASE_ORDER[phase] > PHASE_ORDER[phaseBox.get()]) {
    runInAction(() => phaseBox.set(phase));
  }
}

export function resetStudioPhase(): void {
  if (phaseBox.get() !== 'understand') {
    runInAction(() => phaseBox.set('understand'));
  }
}

/**
 * Marks the document as being inside the studio.
 *
 * The composer dock is hidden unconditionally in `theme.css` (nothing depends on
 * this attribute for that), so this is purely a hook for styling and for anyone
 * inspecting the running page to see which screen family is mounted.
 */
export function markStudioEngaged(): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.smnStudio = 'active';
  }
}

export function currentStudioPhase(): StudioPhase {
  return phaseBox.get();
}

export function readPhaseProp(value: unknown): StudioPhase {
  return value === 'discover' || value === 'decide' ? value : 'understand';
}

export interface StudioStateInput {
  /** Which screen is mounted: the landing, or the working environment. */
  screen: 'entry' | 'studio';
  /** Real run state (`MessagesStore.userRequestPending`) — not a model claim. */
  generating: boolean;
  /** A human question is open in the agent panel. */
  hasQuestion: boolean;
  /** A decision block has actually been written to the canvas. */
  hasDecision: boolean;
}

export function deriveStudioState({
  screen,
  generating,
  hasQuestion,
  hasDecision,
}: StudioStateInput): StudioUiState {
  if (screen === 'entry') {
    return 'understand';
  }
  if (generating) {
    return 'working';
  }
  if (hasDecision && !hasQuestion) {
    return 'complete';
  }
  return 'needs_input';
}

/**
 * Whether the panel offers "Keep exploring" — handing the turn straight back so
 * the agent carries on without the visitor having to type a sentence that carries
 * no information.
 *
 * All three conditions matter, and the middle one is the point:
 *  - `needs_input`: the ball is with the visitor and the engagement is still live.
 *    Excludes `working` (nothing to hand back mid-run) and `complete` (a decision
 *    is on the canvas; there is nothing left to explore).
 *  - no open question: the moment the agent judges the strategy spent it asks a
 *    real question — and then "keep going" is the wrong answer, because the
 *    direction or their own source material is exactly what it now needs. So this
 *    must NOT be available at a strategic pivot.
 *  - `hasGround`: something established to work from — either findings on the
 *    canvas or a brief with rows in it. This deliberately INCLUDES the end of
 *    intake, where the agent has the brief, has nothing to ask, and says it is
 *    about to start: that hand-back is the most common instance of the friction
 *    this action exists to remove, so requiring findings would miss it.
 */
export function offersKeepExploring({
  state,
  hasQuestion,
  hasGround,
}: {
  state: StudioUiState;
  hasQuestion: boolean;
  hasGround: boolean;
}): boolean {
  return state === 'needs_input' && !hasQuestion && hasGround;
}

/**
 * How each state lights the ONE status light.
 * working → breathing pulse · needs_input → brighter steady · otherwise steady.
 */export function statusLightFor(state: StudioUiState): AgentStatus {
  switch (state) {
    case 'working':
      return 'working';
    case 'needs_input':
      return 'needs-input';
    default:
      return 'waiting';
  }
}
